import React from "react";
import { ChoiceChips, Field, FieldDescription, FieldLabel, FieldLegend, FieldSet, Input, Textarea } from "@athena/ui";
import {
    REPORT_BRANDS,
    type ReportAudience,
    type ReportBrandSlug,
    type ReportEvent,
    type ReportNarrative,
} from "./reportContent";

interface ReportContentPanelProps {
    brand: ReportBrandSlug;
    onBrandChange: (brand: ReportBrandSlug) => void;
    narrative: ReportNarrative;
    onNarrativeChange: (narrative: ReportNarrative) => void;
}

interface EventField {
    key: keyof ReportEvent;
    label: string;
    placeholder: string;
    multiline?: boolean;
}

/** The two event cards differ only in the line the template gives each: format, or where it was held. */
const EVENTS = [
    {
        key: "virtualEvent",
        legend: "Virtual",
        detail: { label: "Format", placeholder: "Webinar / Live Q&A / Workshop" },
    },
    {
        key: "inPersonEvent",
        legend: "In-person",
        detail: { label: "Location / chapter", placeholder: "Location" },
    },
] as const;

const eventFields = (detail: { label: string; placeholder: string }): EventField[] => [
    { key: "name", label: "Event / session name", placeholder: "Name" },
    { key: "date", label: "Date", placeholder: "Date" },
    { key: "attendees", label: "Attendees", placeholder: "##" },
    { key: "detail", ...detail },
    { key: "notes", label: "Notes", placeholder: "One sentence on what was covered or how it landed", multiline: true },
];

/**
 * The written half of the partner report: which brand it goes out under, who prepared it, and the
 * template's free-text sections. Every section prints only once something is written in it, so a
 * blank field is how a section is left out.
 */
export const ReportContentPanel: React.FC<ReportContentPanelProps> = ({
    brand,
    onBrandChange,
    narrative,
    onNarrativeChange,
}) => {
    const update = (patch: Partial<ReportNarrative>) => onNarrativeChange({ ...narrative, ...patch });
    const updateAudience = (index: number, patch: Partial<ReportAudience>) =>
        update({
            audiences: narrative.audiences.map((audience, i) => (i === index ? { ...audience, ...patch } : audience)),
        });

    return (
        <details className="bg-card mb-6 rounded-lg border p-6">
            <summary className="cursor-pointer font-medium">Report content</summary>
            <p className="text-text-caption mt-1 text-sm">
                The banner, author and written sections of the PDF. A section left blank is left out.
            </p>

            <div className="mt-6 flex flex-col gap-8">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <Field>
                        <FieldLabel id="report-brand-label">Report banner</FieldLabel>
                        <ChoiceChips
                            aria-labelledby="report-brand-label"
                            value={brand}
                            onValueChange={(value) => onBrandChange(value as ReportBrandSlug)}
                            options={REPORT_BRANDS.map((option) => ({ value: option.slug, label: option.label }))}
                        />
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="report-prepared-by">Prepared by</FieldLabel>
                        <Input
                            id="report-prepared-by"
                            value={narrative.preparedBy}
                            placeholder="Your name"
                            onChange={(event) => update({ preparedBy: event.target.value })}
                        />
                    </Field>
                </div>

                <Field>
                    <FieldLabel htmlFor="report-month-in-brief">The month in brief</FieldLabel>
                    <Textarea
                        id="report-month-in-brief"
                        value={narrative.monthInBrief}
                        placeholder="This month, [X] students at [Institution] were active, [up/down] from last month. [One plain-language observation]. Here's what it means for your students."
                        onChange={(event) => update({ monthInBrief: event.target.value })}
                    />
                </Field>

                <div>
                    <p className="mb-3 font-medium">Virtual & in-person engagement</p>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        {EVENTS.map((eventCard) => {
                            const event = narrative[eventCard.key];
                            return (
                                <FieldSet key={eventCard.key} className="gap-3">
                                    <FieldLegend variant="label">{eventCard.legend}</FieldLegend>
                                    {eventFields(eventCard.detail).map((field) => {
                                        const id = `report-${eventCard.key}-${field.key}`;
                                        const onChange = (value: string) =>
                                            update({ [eventCard.key]: { ...event, [field.key]: value } });
                                        return (
                                            <Field key={field.key}>
                                                <FieldLabel htmlFor={id}>
                                                    {field.label}
                                                    <span className="sr-only"> ({eventCard.legend})</span>
                                                </FieldLabel>
                                                {field.multiline ? (
                                                    <Textarea
                                                        id={id}
                                                        value={event[field.key]}
                                                        placeholder={field.placeholder}
                                                        onChange={(e) => onChange(e.target.value)}
                                                    />
                                                ) : (
                                                    <Input
                                                        id={id}
                                                        value={event[field.key]}
                                                        placeholder={field.placeholder}
                                                        onChange={(e) => onChange(e.target.value)}
                                                    />
                                                )}
                                            </Field>
                                        );
                                    })}
                                </FieldSet>
                            );
                        })}
                    </div>
                </div>

                <FieldSet className="gap-3">
                    <FieldLegend variant="label">Student stories & testimonials</FieldLegend>
                    <Field>
                        <FieldLabel htmlFor="report-testimonial-quote">Quote</FieldLabel>
                        <Textarea
                            id="report-testimonial-quote"
                            value={narrative.testimonial.quote}
                            placeholder="Student quote in their own words. Even one or two sentences is enough."
                            onChange={(event) =>
                                update({ testimonial: { ...narrative.testimonial, quote: event.target.value } })
                            }
                        />
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="report-testimonial-attribution">Attribution</FieldLabel>
                        <Input
                            id="report-testimonial-attribution"
                            value={narrative.testimonial.attribution}
                            placeholder="Anonymized identifier, e.g. Junior, IFC Chapter"
                            onChange={(event) =>
                                update({ testimonial: { ...narrative.testimonial, attribution: event.target.value } })
                            }
                        />
                    </Field>
                </FieldSet>

                <div>
                    <p className="mb-1 font-medium">What this means for you</p>
                    <FieldDescription className="mb-3">
                        One card per audience. A card with no text is left out, and one with no heading prints without
                        one.
                    </FieldDescription>
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                        {narrative.audiences.map((audience, index) => {
                            const position = `audience ${index + 1}`;
                            return (
                                <FieldSet key={index} className="gap-3">
                                    <Field>
                                        <FieldLabel htmlFor={`report-audience-${index}-title`}>
                                            Heading<span className="sr-only"> ({position})</span>
                                        </FieldLabel>
                                        <Input
                                            id={`report-audience-${index}-title`}
                                            value={audience.title}
                                            onChange={(event) => updateAudience(index, { title: event.target.value })}
                                        />
                                    </Field>
                                    <Field>
                                        <FieldLabel htmlFor={`report-audience-${index}-body`}>
                                            Observation<span className="sr-only"> ({position})</span>
                                        </FieldLabel>
                                        <Textarea
                                            id={`report-audience-${index}-body`}
                                            value={audience.body}
                                            onChange={(event) => updateAudience(index, { body: event.target.value })}
                                        />
                                    </Field>
                                    <Field>
                                        <FieldLabel htmlFor={`report-audience-${index}-next-step`}>
                                            Recommended next step<span className="sr-only"> ({position})</span>
                                        </FieldLabel>
                                        <Textarea
                                            id={`report-audience-${index}-next-step`}
                                            value={audience.nextStep}
                                            onChange={(event) =>
                                                updateAudience(index, { nextStep: event.target.value })
                                            }
                                        />
                                    </Field>
                                </FieldSet>
                            );
                        })}
                    </div>
                </div>
            </div>
        </details>
    );
};
